import {
  ClipboardEvent,
  FunctionComponent,
  forwardRef,
  PropsWithoutRef,
  Ref,
  useRef,
  RefObject,
  ReactNode,
  Dispatch,
  SetStateAction,
  useImperativeHandle,
  useEffect
} from 'react';
import styled from 'styled-components';

import { defaultThemeProp } from '../../../theme';
import { useConfiguration, useFocusWithin, useI18n } from '../../../hooks';
import { ForwardProps } from '../../../types';
import FormField from '../../FormField';
import Flex from '../../Flex';
import Button from '../../Button';
import Icon, { registerIcon } from '../../Icon';
import * as timesIcon from '../../Icon/icons/times.icon';
import * as calendarIcon from '../../Icon/icons/calendar.icon';
import DatePicker from '../Picker/DatePicker';
import Popover from '../../Popover';
import { StyledFormControl } from '../../FormControl';
import DateTimeProps, { DateTimeCallbackParameter } from '../DateTime.types';

import {
  getDateFormat,
  parsePastedTextToDate,
  parseToDate,
  validateDate,
  generatePickerButtonAriaLabel
} from './utils';
import StyledDateTime, { StyledInputContainer } from './DateTime.styles';
import useAutoFocusNextInput from './useAutoFocusNextInput';
import PartInput from './PartInput';
import usePickerButton from './usePickerButton';

registerIcon(timesIcon, calendarIcon);

export interface DateInputProps extends DateTimeProps {
  /**
   * Display additional column with week number in date picker popover.
   * @default false
   */
  showWeekNumber?: boolean;
  /** Changes default date picker */
  picker?: ReactNode;
}

export interface DateInputRefObject {
  readonly value: Date | undefined;
  setOpen: Dispatch<SetStateAction<boolean>>;
  buttonEl: HTMLElement | null;
}

export const StyledDateInput = styled(StyledDateTime)<DateInputProps>`
  padding-inline-end: 0;
`;

StyledDateInput.defaultProps = defaultThemeProp;

const convertToCallbackParameter = (
  date: Date,
  parts: (string | undefined)[]
): DateTimeCallbackParameter => {
  if (parts.every(Boolean)) {
    const [year, monthIndex, day] = parts.map(Number);

    if (!validateDate(year, monthIndex + 1, day)) {
      return {
        valueAsISOString: '',
        valueAsTimestamp: NaN,
        state: 'invalid'
      };
    }

    date.setUTCFullYear(year, monthIndex, day);
    return {
      valueAsISOString: date.toISOString(),
      valueAsTimestamp: date.getTime()
    };
  }
  if (parts.every(p => !p)) {
    return {
      valueAsISOString: '',
      valueAsTimestamp: undefined
    };
  }
  return {
    valueAsISOString: '',
    valueAsTimestamp: NaN,
    state: 'incomplete'
  };
};

const DateInput: FunctionComponent<DateInputProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<DateInputProps>, ref: Ref<DateInputRefObject>) => {
    const { locale } = useConfiguration();
    const {
      defaultValue,
      value = defaultValue,
      min,
      max,
      id,
      label,
      labelHidden,
      info = getDateFormat(locale).join(''),
      status,
      required,
      readOnly,
      disabled,
      onChange,
      onFocus,
      onBlur,
      showWeekNumber,
      picker,
      autoFocus,
      ...restProps
    } = props;

    const t = useI18n();

    const date = value !== undefined ? parseToDate(value) : undefined;

    const [yearInputRef, monthInputRef, dayInputRef] = [
      useRef<HTMLInputElement>(null),
      useRef<HTMLInputElement>(null),
      useRef<HTMLInputElement>(null)
    ];

    const pickParts = () => {
      const month = monthInputRef.current?.value
        ? (Number(monthInputRef.current.value) - 1).toString()
        : undefined;
      return [
        yearInputRef.current?.value || undefined,
        month,
        dayInputRef.current?.value || undefined
      ];
    };

    const currentlySelectedDate = () => {
      const parts = pickParts();
      if (parts.every(Boolean)) {
        const [year, monthIndex, day] = parts.map(Number);

        return validateDate(year, monthIndex + 1, day)
          ? new Date(Date.UTC(year, monthIndex, day))
          : undefined;
      }
    };

    const inputContainerRef = useRef<HTMLDivElement>(null);

    const onFocusChange = (focused: boolean) => {
      const callbackParam = convertToCallbackParameter(
        date ? new Date(date) : new Date(),
        pickParts()
      );
      if (onFocus && focused) onFocus(callbackParam);
      if (!focused) {
        if (inputContainerRef.current) inputContainerRef.current.scrollTop = 0;
        onBlur?.(callbackParam);
      }
    };

    const [open, setOpen, , setPopoverRef, buttonEl, setButtonEl] = usePickerButton();

    const onInputChange = () => {
      onChange?.(convertToCallbackParameter(date ? new Date(date) : new Date(), pickParts()));
      buttonEl?.setAttribute(
        'aria-label',
        generatePickerButtonAriaLabel(currentlySelectedDate(), 'date', locale, t, open)
      );
    };

    const setInputValue = (newValue: Date) => {
      dayInputRef.current!.value = newValue.getUTCDate().toString().padStart(2, '0');
      monthInputRef.current!.value = (newValue.getUTCMonth() + 1).toString().padStart(2, '0');
      yearInputRef.current!.value = newValue.getUTCFullYear().toString();
    };

    const onPaste = (e: ClipboardEvent) => {
      const text = e.clipboardData.getData('text');
      const newDate = parsePastedTextToDate(text, locale, date || new Date());
      if (newDate) {
        e.preventDefault();
        setInputValue(newDate);
        onInputChange();
      }
    };

    const DayInput = (
      <PartInput
        key='day'
        placeholder={t('day_placeholder')}
        aria-label={t('day_label_a11y')}
        value={date?.getUTCDate()}
        onChange={onInputChange}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        min={1}
        max={31}
        ref={dayInputRef}
      />
    );

    const MonthInput = (
      <PartInput
        key='month'
        placeholder={t('month_placeholder')}
        aria-label={t('month_label_a11y')}
        value={date ? date?.getUTCMonth() + 1 : undefined}
        onChange={onInputChange}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        min={1}
        max={12}
        ref={monthInputRef}
      />
    );

    const YearInput = (
      <PartInput
        key='year'
        placeholder={t('year_placeholder')}
        aria-label={t('year_label_a11y')}
        value={date?.getUTCFullYear()}
        onChange={onInputChange}
        size={4}
        maxLength={4}
        readOnly={readOnly}
        min={1}
        ref={yearInputRef}
      />
    );

    const containerRef = useRef<HTMLElement>(null);
    useFocusWithin([containerRef], onFocusChange);

    useImperativeHandle(ref, () => ({
      setOpen,
      buttonEl,
      get value() {
        return currentlySelectedDate();
      }
    }));

    const Picker = (
      <DatePicker
        selected={currentlySelectedDate()}
        onChange={selected => {
          setInputValue(selected);
          setOpen(false);
          setTimeout(() => buttonEl?.focus());
          onInputChange();
        }}
        min={min}
        max={max}
        showWeekNumber={showWeekNumber}
      />
    );

    const PickerButton = (
      <>
        <Button
          variant='simple'
          icon
          ref={setButtonEl}
          onClick={() => setOpen(cur => !cur)}
          aria-label={generatePickerButtonAriaLabel(
            currentlySelectedDate(),
            'date',
            locale,
            t,
            open
          )}
          readOnly={readOnly}
          disabled={disabled}
        >
          <Icon name={open ? 'times' : 'calendar'} />
        </Button>

        <Popover
          show={open}
          ref={setPopoverRef}
          target={containerRef.current}
          placement='bottom-end'
          strategy='fixed'
          modifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-end', 'right', 'left']
              }
            }
          ]}
        >
          {picker || Picker}
        </Popover>
      </>
    );

    const orderedRefs: RefObject<HTMLInputElement>[] = [];

    const Comp = (
      <Flex
        as={StyledDateInput}
        forwardedAs={StyledFormControl}
        container={{ alignItems: 'center', wrap: 'nowrap' }}
        status={status}
        readOnly={readOnly}
        disabled={disabled}
        ref={containerRef}
        onClick={(e: MouseEvent) => {
          if (e.target === containerRef.current) orderedRefs[0].current?.focus();
        }}
        {...restProps}
      >
        <Flex
          ref={inputContainerRef}
          as={StyledInputContainer}
          container={{ alignItems: 'center', wrap: 'wrap' }}
          onPaste={onPaste}
        >
          {getDateFormat(locale).map(part => {
            switch (part) {
              case 'DD':
                orderedRefs.push(dayInputRef);
                return DayInput;
              case 'MM':
                orderedRefs.push(monthInputRef);
                return MonthInput;
              case 'YYYY':
                orderedRefs.push(yearInputRef);
                return YearInput;
              default:
                return part;
            }
          })}
        </Flex>
        {!readOnly && PickerButton}
      </Flex>
    );

    useEffect(() => {
      if (autoFocus) orderedRefs[0].current?.focus();
    }, [autoFocus]);

    useAutoFocusNextInput(orderedRefs);

    return label ? (
      <FormField
        as='fieldset'
        labelAs='legend'
        {...{ label, labelHidden, id, info, status, required, disabled }}
        ref={ref}
      >
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default DateInput;
